/**
 * Decision Unit Resolver — 10I
 *
 * Constructs a DecisionUnit from existing data across the system.
 * A Decision Unit is computed — it is not stored as a single record.
 *
 * The resolver aggregates:
 * - Context and configuration data
 * - Track, session, and session_result data
 * - Pool and criteria state (from evaluate_context_readiness RPC)
 * - Scoring state (accepted_values)
 * - Catchball loop state
 * - Governance events and lineage
 *
 * Every query here names columns that exist (checked against production on
 * 2026-10-07): the earlier version read `accepted_values.track_id`,
 * `session_results.ranked_items`, `cycle_item_participation.locked` and
 * others that never existed, so the Decision Unit panel said "No values
 * entered yet · Not yet produced" for every session, however far along.
 *
 * Canon reference: DOCS_V4/00-canon/02-canonical-domain-model.md
 * Implementation brief: DOCS_V4/00-canon/10I-cursor-decision-unit-runtime.md
 */

import { getSafeSupabaseClient } from './supabaseSync';
import type { SupabaseClient } from '@supabase/supabase-js';
import type { PrioritizationContext } from '../types/prioritization/context';
import type {
  DecisionUnit,
  DecisionUnitFrame,
  DecisionUnitDecision,
  DecisionUnitProvenance,
} from '../types/prioritization/decisionUnit';

// ============================================================================
// Frame builder
// ============================================================================

async function buildFrame(
  context: PrioritizationContext,
  trackIds: string[],
  supabase: SupabaseClient
): Promise<DecisionUnitFrame> {
  const primaryTrackId = trackIds[0] ?? null;

  const [participantsResult, poolsResult, trackResult, attributeResult] = await Promise.all([
    // S1 — Participation: the track's people when there is a track (a cycle's
    // participation is set per track), else the cycle's.
    primaryTrackId
      ? supabase
          .from('track_participants')
          .select('user_id, role')
          .eq('track_id', primaryTrackId)
          .is('removed_at', null)
      : context.cycle_id
        ? supabase
            .from('cycle_participants')
            .select('user_id, role')
            .eq('cycle_id', context.cycle_id)
            .is('removed_at', null)
        : Promise.resolve({ data: [] as Array<{ user_id: string; role: string | null }>, error: null }),

    // S2 — Pool: the track's item pools (membership is read next; a pool is
    // locked when pool_locked_at is stamped — there is no per-item lock).
    primaryTrackId
      ? supabase
          .from('item_pools')
          .select('id, pool_locked_at')
          .eq('track_id', primaryTrackId)
      : Promise.resolve({ data: [] as Array<{ id: string; pool_locked_at: string | null }>, error: null }),

    // S3 — Lock stamps: participation on the track, criteria on the session baseline
    // (Phase C-1, canon 28 §6).
    primaryTrackId
      ? supabase
          .from('tracks')
          .select('participation_locked_at, session_baselines(criteria_locked_at)')
          .eq('id', primaryTrackId)
          .single()
      : Promise.resolve({ data: null, error: null }),

    // S3/S4 — Criteria and their scales: the cycle's attribute configs carry the
    // chosen attribute keys and, per key, whether a range has been accepted.
    context.cycle_id
      ? supabase
          .from('cycle_attribute_configs')
          .select('attribute_key, range_status, range_value, track_id')
          .eq('cycle_id', context.cycle_id)
      : Promise.resolve({ data: [] as Array<{ attribute_key: string; range_status: string | null; range_value: unknown; track_id: string | null }>, error: null }),
  ]);

  const participants = participantsResult.data ?? [];
  const pools = (poolsResult.data ?? []) as Array<{ id: string; pool_locked_at: string | null }>;
  const trackData = trackResult.data as { participation_locked_at?: string | null; session_baselines?: unknown } | null;
  const rawTrackBaseline = trackData?.session_baselines;
  const trackBaseline = (Array.isArray(rawTrackBaseline) ? rawTrackBaseline[0] : rawTrackBaseline) as
    | { criteria_locked_at?: string | null }
    | undefined;
  const attributeRows = (attributeResult.data ?? []) as Array<{
    attribute_key: string; range_status: string | null; range_value: unknown; track_id: string | null;
  }>;

  let poolItemIds: string[] = [];
  if (pools.length > 0) {
    const { data: members } = await supabase
      .from('item_pool_memberships')
      .select('item_id')
      .in('item_pool_id', pools.map((pool) => pool.id));
    poolItemIds = Array.from(new Set(((members ?? []) as Array<{ item_id: string }>).map((m) => m.item_id)));
  }

  // Configs scoped to another track belong to another frame.
  const scopedAttributes = attributeRows.filter(
    (row) => !row.track_id || !primaryTrackId || row.track_id === primaryTrackId,
  );
  const criteriaKeys = Array.from(new Set(scopedAttributes.map((row) => row.attribute_key)));
  const scalesDefined: Record<string, boolean> = {};
  for (const key of criteriaKeys) {
    scalesDefined[key] = scopedAttributes.some(
      (row) => row.attribute_key === key && (row.range_status === 'accepted' || row.range_value != null),
    );
  }

  const poolLocked = pools.length > 0 && pools.every((pool) => !!pool.pool_locked_at);

  return {
    participation: {
      participantCount: participants.length,
      participantIds: participants.map((p: { user_id: string }) => p.user_id),
      locked: !!trackData?.participation_locked_at,
    },
    pool: {
      itemCount: poolItemIds.length,
      itemIds: poolItemIds,
      locked: poolLocked,
      sourceIds: pools.map((pool) => pool.id),
    },
    criteria: {
      criteriaCount: criteriaKeys.length,
      criteriaKeys,
      selected: criteriaKeys.length > 0,
      locked: !!trackBaseline?.criteria_locked_at,
    },
    expression: {
      allScalesDefined: criteriaKeys.length > 0 && Object.values(scalesDefined).every(Boolean),
      // No dedicated weights table — an accepted range per criterion is the value expression.
      allWeightsDefined: criteriaKeys.length > 0 && Object.values(scalesDefined).every(Boolean),
      scalesDefined,
    },
  };
}

// ============================================================================
// Decision builder
// ============================================================================

/** The ranked rows of a session, in rank order, in the shape the unit carries. */
export function rankedItemsFromResults(
  rows: ReadonlyArray<{ item_id: string; rank: number | null; score: number | null }>,
): Array<{ itemId: string; rank: number; score?: number }> {
  return rows
    .filter((r) => r.rank != null)
    .sort((x, y) => (x.rank as number) - (y.rank as number))
    .map((r) => ({ itemId: r.item_id, rank: r.rank as number, ...(r.score != null ? { score: r.score } : {}) }));
}

async function buildDecision(
  context: PrioritizationContext,
  trackIds: string[],
  poolItemIds: readonly string[],
  supabase: SupabaseClient
): Promise<DecisionUnitDecision> {
  const primaryTrackId = trackIds[0] ?? null;

  // S6 — the latest published session for this cycle (and track, when there is one).
  let latestSession: { id: string; published_at: string } | null = null;
  if (context.cycle_id) {
    let query = supabase
      .from('prioritization_sessions')
      .select('id, published_at')
      .eq('cycle_id', context.cycle_id)
      .not('published_at', 'is', null)
      .is('retired_at', null)
      .order('published_at', { ascending: false })
      .limit(1);
    if (primaryTrackId) query = query.eq('track_id', primaryTrackId);
    const { data } = await query;
    latestSession = ((data ?? []) as Array<{ id: string; published_at: string }>)[0] ?? null;
  }

  const [valuesResult, rankingResult, overridesResult, govResult, trackConvergenceResult] = await Promise.all([
    // S5 — Official values on the pool's items (accepted_values is keyed by item,
    // not track). A session's value never stands in for the official one, so
    // session-scoped rows are left out (roadmap 1C, V-3).
    poolItemIds.length > 0
      ? supabase
          .from('accepted_values')
          .select('item_id, attribute_key')
          .in('item_id', [...poolItemIds])
          .is('session_scope', null)
      : Promise.resolve({ data: [] as Array<{ item_id: string; attribute_key: string }>, error: null }),

    // S6 — The published session's ranked rows
    latestSession
      ? supabase
          .from('session_results')
          .select('item_id, rank, score')
          .eq('session_id', latestSession.id)
      : Promise.resolve({ data: [] as Array<{ item_id: string; rank: number | null; score: number | null }>, error: null }),

    // Governance overrides
    context.cycle_id
      ? supabase
          .from('governance_events')
          .select('id')
          .eq('cycle_id', context.cycle_id)
          .eq('event_type', 'ranking_override')
      : Promise.resolve({ data: [] as Array<{ id: string }>, error: null }),

    // Has any governance record at all
    context.cycle_id
      ? supabase
          .from('governance_events')
          .select('id')
          .eq('cycle_id', context.cycle_id)
          .limit(1)
      : Promise.resolve({ data: [] as Array<{ id: string }>, error: null }),

    // Track convergence flag (values_converged_at) — Phase C-1: read the
    // Session Baseline, the sole home of the lock stamp.
    primaryTrackId
      ? supabase
          .from('session_baselines')
          .select('values_converged_at')
          .eq('track_id', primaryTrackId)
          .maybeSingle()
      : Promise.resolve({ data: null, error: null }),
  ]);

  const values = (valuesResult.data ?? []) as Array<{ item_id: string; attribute_key: string }>;
  const rankedRows = (rankingResult.data ?? []) as Array<{ item_id: string; rank: number | null; score: number | null }>;
  const overrides = overridesResult.data ?? [];
  const govRows = govResult.data ?? [];
  const trackConvergence = trackConvergenceResult.data;

  const rankedItems = rankedItemsFromResults(rankedRows);

  // Unique items that have at least one accepted value
  const scoredItemIds = new Set(values.map((v) => v.item_id));

  return {
    scoring: {
      valuesSufficient: scoredItemIds.size > 0,
      valuesConverged: !!(trackConvergence as { values_converged_at: string | null } | null)?.values_converged_at,
      scoredItemCount: scoredItemIds.size,
      totalItemCount: poolItemIds.length > 0 ? poolItemIds.length : scoredItemIds.size,
    },
    ranking: {
      hasPublishedRanking: !!latestSession && rankedItems.length > 0,
      sessionId: latestSession?.id ?? undefined,
      rankedItems,
      publishedAt: latestSession?.published_at ?? undefined,
    },
    rationale: {
      hasOverrides: overrides.length > 0,
      overrideCount: overrides.length,
      hasGovernanceRecord: govRows.length > 0,
    },
  };
}

// ============================================================================
// Provenance builder
// ============================================================================

/** What the evaluator did with a loop, read from the loop's own state. */
export function evaluatorDecisionFromState(state: string | null | undefined): 'accepted' | 'revised' | 'rejected' | undefined {
  const value = (state ?? '').toLowerCase();
  if (!value) return undefined;
  if (value.includes('accept')) return 'accepted';
  if (value.includes('return') || value.includes('revis')) return 'revised';
  if (value.includes('reject') || value.includes('declin')) return 'rejected';
  return undefined;
}

async function buildProvenance(
  context: PrioritizationContext,
  trackIds: string[],
  poolItemIds: readonly string[],
  supabase: SupabaseClient
): Promise<DecisionUnitProvenance> {
  const primaryTrackId = trackIds[0] ?? null;
  const [sessionsResult, loopsResult, govResult, lineageResult] = await Promise.all([
    // Sessions for this context: by cycle (and track) when the context is a cycle,
    // else the sessions that name the context.
    (() => {
      let query = supabase
        .from('prioritization_sessions')
        .select('id, canonical_type, intent, state, updated_at, run_completed_at, published_at')
        .is('retired_at', null)
        .order('created_at', { ascending: true });
      if (context.cycle_id) {
        query = query.eq('cycle_id', context.cycle_id);
        if (primaryTrackId) query = query.eq('track_id', primaryTrackId);
      } else {
        query = query.eq('context_id', context.id);
      }
      return query;
    })(),

    // Catchball loops for the cycle
    context.cycle_id
      ? supabase
          .from('catchball_loops')
          .select('id, state, intent, synthesis, request_issued_at, proposal_submitted_at')
          .eq('cycle_id', context.cycle_id)
          .order('created_at', { ascending: true })
      : Promise.resolve({ data: [] as Array<{
          id: string; state: string; intent: unknown; synthesis: unknown;
          request_issued_at: string | null; proposal_submitted_at: string | null;
        }>, error: null }),

    // Governance events for the cycle
    context.cycle_id
      ? supabase
          .from('governance_events')
          .select('event_type, occurred_at, actor_id, note')
          .eq('cycle_id', context.cycle_id)
          .order('occurred_at', { ascending: true })
      : Promise.resolve({ data: [] as Array<{
          event_type: string; occurred_at: string; actor_id: string | null; note: string | null;
        }>, error: null }),

    // Lineage events touching the pool's items (lineage_events has no cycle column;
    // it names items on both sides).
    poolItemIds.length > 0
      ? supabase
          .from('lineage_events')
          .select('event_type, source_item_ids, target_item_ids, created_at')
          .eq('workspace_id', context.workspace_id)
          .order('created_at', { ascending: true })
      : Promise.resolve({ data: [] as Array<{
          event_type: string; source_item_ids: string[]; target_item_ids: string[]; created_at: string;
        }>, error: null }),
  ]);

  type SessionRow = { id: string; canonical_type: string | null; intent: string | null; state: string; updated_at: string; run_completed_at: string | null; published_at: string | null };
  type LoopRow = { id: string; state: string; intent: { strategicContext?: string } | string | null; synthesis: string | null; request_issued_at: string | null; proposal_submitted_at: string | null };
  type GovRow = { event_type: string; occurred_at: string; actor_id: string | null; note: string | null };
  type LineageRow = { event_type: string; source_item_ids: string[] | null; target_item_ids: string[] | null; created_at: string };

  const sessions = (sessionsResult.data ?? []) as SessionRow[];
  const loops = (loopsResult.data ?? []) as LoopRow[];
  const govEvents = (govResult.data ?? []) as GovRow[];
  const poolSet = new Set(poolItemIds);
  const lineageEvents = ((lineageResult.data ?? []) as LineageRow[]).filter(
    (e) => (e.source_item_ids ?? []).some((id) => poolSet.has(id)) || (e.target_item_ids ?? []).some((id) => poolSet.has(id)),
  );

  const VALID_LINEAGE_TYPES = ['split', 'merge', 'supersede', 'retire', 'fork'] as const;
  type LineageEventType = typeof VALID_LINEAGE_TYPES[number];

  return {
    sessions: sessions.map((s) => ({
      sessionId: s.id,
      sessionType: s.canonical_type ?? s.intent ?? 'unknown',
      state: s.state,
      completedAt:
        s.published_at ?? s.run_completed_at ?? (s.state === 'PUBLISHED' || s.state === 'RUN_COMPLETE' ? s.updated_at : undefined) ?? undefined,
    })),
    catchballLoops: loops.map((l) => ({
      loopId: l.id,
      state: l.state,
      intent: typeof l.intent === 'string' ? l.intent : l.intent?.strategicContext ?? undefined,
      synthesis: l.synthesis ?? undefined,
      tossedAt: l.request_issued_at ?? undefined,
      returnedAt: l.proposal_submitted_at ?? undefined,
      evaluatorDecision: evaluatorDecisionFromState(l.state),
    })),
    governanceEvents: govEvents.map((e) => ({
      eventType: e.event_type,
      occurredAt: e.occurred_at,
      actorId: e.actor_id ?? '',
      description: e.note ?? undefined,
    })),
    lineageEvents: lineageEvents
      .filter((e) => VALID_LINEAGE_TYPES.includes(e.event_type as LineageEventType))
      .map((e) => ({
        eventType: e.event_type as LineageEventType,
        sourceItemIds: e.source_item_ids ?? [],
        targetItemIds: e.target_item_ids ?? [],
        occurredAt: e.created_at,
      })),
  };
}

// ============================================================================
// Catchball state
// ============================================================================

async function getCatchballState(
  context: PrioritizationContext,
  supabase: SupabaseClient
): Promise<DecisionUnit['catchballState'] | null> {
  if (!context.cycle_id) return null;

  const { data: loops } = await supabase
    .from('catchball_loops')
    .select('id, state, ball_holder_scope')
    .eq('cycle_id', context.cycle_id)
    .order('created_at', { ascending: false })
    .limit(10);

  const activeLoop = ((loops ?? []) as Array<{ id: string; state: string; ball_holder_scope: string | null }>)
    .find((loop) => !loopIsSettled(loop.state));
  if (!activeLoop) return null;

  return {
    active: true,
    currentLoopId: activeLoop.id,
    ballHolder: normalizeBallHolder(activeLoop.ball_holder_scope, activeLoop.state),
  };
}

/** A loop that was accepted, rejected or closed has no ball in flight. */
export function loopIsSettled(state: string | null | undefined): boolean {
  const s = (state ?? '').toLowerCase();
  return ['accept', 'reject', 'declin', 'closed', 'withdrawn', 'cancel', 'retired'].some((w) => s.includes(w));
}

/** The executive tier evaluates; every tier below works; anything else is in transit. */
export function normalizeBallHolder(
  scope: string | null,
  state: string
): 'evaluator' | 'worker' | 'in_transit' | undefined {
  const s = (state ?? '').toLowerCase();
  if (s.includes('returned') || s.includes('review') || s.includes('proposed')) return 'evaluator';
  if (scope === 'executive' || scope === 'top') return 'evaluator';
  if (scope === 'middle' || scope === 'bottom' || scope === 'front_line') return 'worker';
  if (s.includes('working') || s.includes('open') || s.includes('drafting')) return 'worker';
  return 'in_transit';
}

// ============================================================================
// Last activity
// ============================================================================

function getLastActivity(provenance: DecisionUnitProvenance): string | undefined {
  const timestamps: string[] = [
    ...provenance.sessions.map((s) => s.completedAt).filter(Boolean),
    ...provenance.catchballLoops.map((l) => l.returnedAt ?? l.tossedAt).filter(Boolean),
    ...provenance.governanceEvents.map((e) => e.occurredAt),
  ] as string[];

  if (timestamps.length === 0) return undefined;
  return timestamps.reduce((latest, ts) => (ts > latest ? ts : latest));
}

// ============================================================================
// Public API
// ============================================================================

/** The caller's track first; the rest keep their order. */
export function orderTracks(trackIds: readonly string[], preferred?: string | null): string[] {
  if (!preferred || !trackIds.includes(preferred)) return [...trackIds];
  return [preferred, ...trackIds.filter((id) => id !== preferred)];
}

type StageCompletion = DecisionUnit['stageCompletion'];

/**
 * Which of the six stages are done.
 *
 * `evaluate_context_readiness` (migration 658) answers only two of them for a
 * cycle context — participation (from cycle_participants) and final ranking
 * (from a force_ranking decision) — and returns `false` for the other four
 * whatever the data says. So each stage is read from what the Frame and
 * Decision actually hold, and the function's answer counts where it is a
 * real check. When the function grows real checks for all six, this becomes
 * a pass-through.
 */
export function deriveStageCompletion(
  fromRpc: Partial<StageCompletion> | null | undefined,
  frame: DecisionUnitFrame,
  decision: DecisionUnitDecision,
): StageCompletion {
  const rpc = fromRpc ?? {};
  return {
    participation: !!rpc.participation || frame.participation.locked || frame.participation.participantCount > 0,
    pool_formation: !!rpc.pool_formation || frame.pool.locked || frame.pool.itemCount > 0,
    criteria_selection: !!rpc.criteria_selection || frame.criteria.locked || frame.criteria.selected,
    criteria_expression: !!rpc.criteria_expression || frame.expression.allScalesDefined,
    item_scoring: !!rpc.item_scoring || decision.scoring.valuesConverged || decision.scoring.valuesSufficient,
    final_ranking: !!rpc.final_ranking || decision.ranking.hasPublishedRanking,
  };
}

/**
 * Resolve a DecisionUnit from a context ID.
 * This is the primary entry point — every context maps to exactly one Decision Unit.
 */
export interface ResolveDecisionUnitOptions {
  /**
   * The track the caller is looking at. A cycle has several tracks and each
   * has its own pool, criteria and sessions; without this the unit was read
   * against whichever track the database listed first (Sightline's session
   * page read "Security & Compliance" for a Product session and showed
   * nothing).
   */
  trackId?: string | null;
}

export async function resolveDecisionUnit(
  contextId: string,
  _workspaceId: string,
  options: ResolveDecisionUnitOptions = {}
): Promise<DecisionUnit | null> {
  const supabase = getSafeSupabaseClient();
  if (!supabase) return null;

  const { data: context } = await supabase
    .from('prioritization_contexts')
    .select('*')
    .eq('id', contextId)
    .single();
  if (!context) return null;

  // Step 1: Configuration (if present)
  let configuration = null;
  if (context.configuration_id) {
    const { data } = await supabase
      .from('configurations')
      .select('*')
      .eq('id', context.configuration_id)
      .single();
    configuration = data;
  }

  // Step 2: Tracks for cycle contexts — the caller's track first, the rest in a
  // stable order (the database returns an unordered set otherwise).
  let trackIds: string[] = [];
  if (context.cycle_id) {
    const { data: tracks } = await supabase
      .from('tracks')
      .select('id')
      .eq('cycle_id', context.cycle_id)
      .order('created_at', { ascending: true })
      .order('name', { ascending: true });
    trackIds = orderTracks((tracks ?? []).map((t: { id: string }) => t.id), options.trackId);
  }

  // Step 3: Stage completion from RPC (reuse existing — never duplicate logic)
  const { data: readiness } = await supabase.rpc('evaluate_context_readiness', {
    p_context_id: contextId,
  });

  // Step 4: the Frame first — its pool names the items the rest is read against.
  const frame = await buildFrame(context, trackIds, supabase);

  // Steps 5–7: Decision, Provenance, and Catchball state in parallel
  const [decision, provenance, catchballState] = await Promise.all([
    buildDecision(context, trackIds, frame.pool.itemIds, supabase),
    buildProvenance(context, trackIds, frame.pool.itemIds, supabase),
    getCatchballState(context, supabase),
  ]);

  const stageCompletion = deriveStageCompletion(readiness?.stage_completion, frame, decision);

  const complete = Object.values(stageCompletion).every(Boolean);
  const lastActivityAt = getLastActivity(provenance);

  return {
    id: contextId,
    contextId,
    context,
    configuration: configuration ?? undefined,
    trackIds,
    frame,
    decision,
    stageCompletion,
    provenance,
    catchballState: catchballState ?? undefined,
    complete,
    lastActivityAt,
  };
}

/**
 * Resolve a DecisionUnit from a track ID.
 * Looks up the context via the track's cycle, then resolves.
 */
export async function resolveDecisionUnitFromTrack(
  trackId: string,
  workspaceId: string
): Promise<DecisionUnit | null> {
  const supabase = getSafeSupabaseClient();
  if (!supabase) return null;

  const { data: track } = await supabase
    .from('tracks')
    .select('cycle_id')
    .eq('id', trackId)
    .single();
  if (!track?.cycle_id) return null;

  const { data: context } = await supabase
    .from('prioritization_contexts')
    .select('id')
    .eq('cycle_id', track.cycle_id)
    .maybeSingle();
  if (!context) return null;

  return resolveDecisionUnit(context.id, workspaceId);
}

/**
 * Resolve a DecisionUnit from a session ID.
 * Looks up the context via the session's context_id, then resolves.
 */
export async function resolveDecisionUnitFromSession(
  sessionId: string,
  workspaceId: string
): Promise<DecisionUnit | null> {
  const supabase = getSafeSupabaseClient();
  if (!supabase) return null;

  const { data: session } = await supabase
    .from('prioritization_sessions')
    .select('context_id')
    .eq('id', sessionId)
    .single();
  if (!session?.context_id) return null;

  return resolveDecisionUnit(session.context_id, workspaceId);
}
